import { Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Button } from '@zaplify/ui';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import {
    updateImportProspectsReviewDetails,
    updateImportProspectsStep,
    validateImportedProspects,
} from '../../../redux/actions';
import { AppDispatch, RootState, useAppSelector } from '../../../redux/store/configureStore';
import { NavigationBarContainer } from './containers';
import { UploadProspectToProspectCollectionModal } from '../../../components/upload-prospect-to-prospect-collection-modal/upload-prospect-to-prospect-collection';
import { useMsal } from '@azure/msal-react';

const fieldMappingIsValidSelector = (state) => {
    if (state.importProspects?.fieldMapping?.length > 0) {
        return state.importProspects.fieldMapping.some((field) => field.imported !== '');
    } else {
        return false;
    }
};

const shouldButtonBeDisabled = (
    step,
    haveProspectsBeenUploaded,
    fieldMappingValidation,
    numberOfValidProspects,
    validating
) => {
    if (step === 1) return !haveProspectsBeenUploaded;
    if (step === 2) return !fieldMappingValidation;
    if (step === 3) return validating;
    return !(numberOfValidProspects > 0);
};

export const NavigationBar = ({ currentStep, handleStep }) => {
    const dispatch = useDispatch<AppDispatch>();
    const haveProspectsBeenUploaded = useAppSelector(
        (state: RootState) => state.importProspects?.prospects?.length > 0
    );
    const numberOfValidProspects = useAppSelector(
        (state: RootState) => state.importProspects?.reviewDetails?.validProspects?.length || 0
    );
    const validationDetails = useAppSelector((state: RootState) => state.importProspects?.reviewDetails);
    const fieldMappingValidation = useAppSelector(fieldMappingIsValidSelector);
    const [validating, setValidating] = useState(false);
    const [displayProspectCollectionModal, setDisplayProspectCollectionModal] = useState(false);
    const msal = useMsal();

    function getButtonText(step, validProspects) {
        switch (step) {
            case 1:
                return 'Continue to field mapping';
            case 2:
                return 'Continue to review';
            case 3:
                if (validating) {
                    return 'Uploading and searching for matches...';
                }
                return validProspects > 0 ? 'Add prospects to collection' : 'Continue';
                break;
            default:
                return 'Continue';
        }
    }

    const handleNext = async () => {
        console.log('handleNext', currentStep);
        if (currentStep === 1) {
            dispatch(updateImportProspectsReviewDetails({}));
            handleStep(2);
            return;
        }
        if (currentStep === 2) {
            setValidating(true);
            dispatch(validateImportedProspects(msal.instance));
            handleStep(3);
            return;
        }
        if (currentStep === 3) {
            setDisplayProspectCollectionModal(true);
            return;
        }
        dispatch(updateImportProspectsStep(currentStep + 1));
        handleStep(currentStep + 1);
    };
    const handleBack = () => {
        if (currentStep === 3) {
            dispatch(updateImportProspectsReviewDetails({}));
        }
        dispatch(updateImportProspectsStep(currentStep - 1));
        handleStep(currentStep - 1);
    };

    const nextStepButtonDisabled = shouldButtonBeDisabled(
        currentStep,
        haveProspectsBeenUploaded,
        fieldMappingValidation,
        numberOfValidProspects,
        validating
    );
    const nextStepButtonText = getButtonText(currentStep, numberOfValidProspects);

    useEffect(() => {
        if (Object.keys(validationDetails).length > 0) {
            setValidating(false);
        }
    }, [validationDetails]);

    return (
        <NavigationBarContainer>
            {displayProspectCollectionModal && (
                <UploadProspectToProspectCollectionModal
                    onModalClose={() => setDisplayProspectCollectionModal(false)}
                />
            )}
            <div>
                <Typography className="import-prospects-navigation-text-bold">Upload CSV / </Typography>
                <Typography
                    className={
                        currentStep > 1
                            ? 'import-prospects-navigation-text-bold'
                            : 'import-prospects-navigation-text-thin'
                    }
                >
                    Map Fields /{' '}
                </Typography>
                <Typography
                    className={
                        currentStep > 2
                            ? 'import-prospects-navigation-text-bold'
                            : 'import-prospects-navigation-text-thin'
                    }
                >
                    Review
                </Typography>
            </div>
            <div>
                {currentStep > 1 && (
                    <Button
                        className="import-prospects-back-button"
                        variant="text"
                        text="Go back"
                        onClick={handleBack}
                    />
                )}
                <Button
                    height="48px"
                    borderRadius="100px"
                    classes={['import-prospects-next-button', ' regular']}
                    variant="contained"
                    endIcon={validating && <CircularProgress color="inherit" size={20} />}
                    disabled={nextStepButtonDisabled}
                    text={nextStepButtonText}
                    onClick={!validating && handleNext}
                />
            </div>
        </NavigationBarContainer>
    );
};
